/**
 * Optimistic concurrency for a document a page saves WHOLE, straight to
 * Firestore - the page's half.
 *
 * Study design (written by BOTH the Study Design page and the Visits page),
 * the CRF Builder session, form assignments (crfPackage/visitMapping) and DMP
 * section text are each saved in full from the page's own copy. PR #207 found
 * the same shape in CRF form saves: on 2026-09-10 a copy loaded before a
 * dictionary adoption was saved after it and put the old form back.
 *
 * The rules refuse a write whose `docVersion` is not the stored version plus
 * one. This class is what a page needs to live with that rule:
 *
 *  - it holds the version of the copy the page is showing, raised by
 *    `observe()` ONLY where the page actually takes on the stored content.
 *    A page that keeps the user's unsaved text instead (the DMP editor while
 *    the user is typing) must NOT observe a snapshot it did not take on, so
 *    its next save is refused as stale rather than silently replacing the
 *    other edit (features/documents/dmpEditorSync.ts,
 *    shouldObserveSectionVersion);
 *  - `write()` claims that version plus one and runs one save at a time, so two
 *    quick saves of one document never claim the same number;
 *  - a refusal is told apart from a genuine permission problem by reading the
 *    stored version: moved means stale, unmoved means rethrow;
 *  - once stale, nothing more is written until the page is reloaded.
 *
 * Pure - no React, no Firebase - so it can be tested from functions' vitest.
 * versionedFirestoreDoc.ts connects it to a Firestore document.
 */

/** The field the rules check (firestore.rules, docVersionAdvances). */
export const DOC_VERSION_FIELD = 'docVersion';

/** A stored document's version. Missing or malformed counts as 0, as in the rules. */
export function readDocVersion(data: unknown): number {
  if (!data || typeof data !== 'object') return 0;
  const v = (data as Record<string, unknown>)[DOC_VERSION_FIELD];
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0;
}

/** Shown when a save is refused because the document moved underneath it. */
export function docChangedMessage(what: string): string {
  return what + ' was changed elsewhere after you opened it - for example in another tab. ' +
    'Your change was not saved. Reload the page to see the current version, then make your change again.';
}

function codeOf(err: unknown): string {
  if (!err || typeof err !== 'object') return '';
  const c = (err as { code?: unknown }).code;
  return typeof c === 'string' ? c.replace(/^(functions|firestore)\//, '') : '';
}

/** A rules refusal. Firestore spells it 'permission-denied'. */
export function isPermissionDenied(err: unknown): boolean {
  return codeOf(err) === 'permission-denied';
}

/**
 * A server function refusing a save because the stored copy moved: code
 * failed-precondition (either spelling) AND the given reason, so that no other
 * precondition failure is mistaken for it.
 */
export function isRefusedAsChanged(err: unknown, reason: string): boolean {
  if (codeOf(err) !== 'failed-precondition') return false;
  const d = (err as { details?: unknown }).details;
  return !!d && typeof d === 'object' && (d as { reason?: unknown }).reason === reason;
}

/** saved: written. stale: refused, the document had moved. blocked: not attempted, already stale. */
export type WriteOutcome = 'saved' | 'stale' | 'blocked';

/** What `write` reports to its `settled` hook: a WriteOutcome, 'skipped' (the gate said no write) or 'failed' (it threw). */
export type SettledOutcome = WriteOutcome | 'skipped' | 'failed';

/**
 * Hooks that run INSIDE the queue, in turn with the other saves of this document:
 *  - `gate` is asked when the write is about to run, not when it was requested, so a decision
 *    "nothing to write" is made against what the earlier queued saves actually stored;
 *  - `settled` is told the outcome before the next queued save starts.
 */
export interface WriteHooks {
  gate?: () => boolean;
  settled?: (outcome: SettledOutcome) => void;
}

export class VersionedDoc {
  private knownVersion = 0;
  private stale = false;
  /** The latest queued piece of work. Never rejects, so one failure cannot stop the next. */
  private tail: Promise<unknown> = Promise.resolve();

  /**
   * `readCurrentMarker` (optional) reads a marker the saved content carries, so a refusal caused by this
   * write's own earlier copy (acknowledgement lost, write resent) can be told apart from another writer.
   */
  constructor(
    private readonly readCurrentVersion: () => Promise<number>,
    private readonly readCurrentMarker?: () => Promise<string | undefined>,
  ) {}

  /** The version of the copy the page holds. */
  get known(): number { return this.knownVersion; }

  /** True once a save was refused as stale; nothing further is written. */
  get isStale(): boolean { return this.stale; }

  /**
   * The page has taken on the stored content at this version. Never moves the
   * version backwards: Firestore delivers snapshots in order, and a page's own
   * pending write shows up at once with the number it is claiming.
   */
  observe(version: number): void {
    if (version > this.knownVersion) this.knownVersion = version;
  }

  /**
   * Something this page caused moved the stored version from `before` to
   * `after` without changing what the page holds (a one-field update, or a
   * server call made on the page's behalf). Adopted only if it was based on
   * the page's own version; otherwise the document had moved independently
   * and the page's copy is NOT current.
   */
  adoptIfCurrent(before: number, after: number): void {
    if (before === this.knownVersion && after > this.knownVersion) this.knownVersion = after;
  }

  /**
   * The page has reloaded the stored content and now holds it: lift the stale
   * block and base the next save on that stored version. Only for a caller that
   * has merged its own work onto what it just read (features/crf/bulkVariableMerge.ts).
   */
  recover(storedVersion: number): void {
    this.knownVersion = storedVersion;
    this.stale = false;
  }

  /** Run work that moves the stored version, in turn with this document's saves. */
  exclusive<T>(task: () => Promise<T>): Promise<T> {
    const run = this.tail.then(task, task);
    this.tail = run.catch(() => undefined);
    return run;
  }

  /**
   * Save, claiming the known version plus one. `task` performs the write and
   * must put `next` in the document's docVersion field.
   */
  write(task: (next: number) => Promise<void>, marker?: string, hooks?: WriteHooks): Promise<WriteOutcome | 'skipped'> {
    return this.exclusive(async (): Promise<WriteOutcome | 'skipped'> => {
      let outcome: SettledOutcome = 'failed';
      try {
        const r = await this.writeNow(task, marker, hooks);
        outcome = r;
        return r;
      } finally {
        hooks?.settled?.(outcome);
      }
    });
  }

  private async writeNow(task: (next: number) => Promise<void>, marker: string | undefined, hooks: WriteHooks | undefined): Promise<WriteOutcome | 'skipped'> {
    {
      if (this.stale) return 'blocked';
      if (hooks?.gate && !hooks.gate()) return 'skipped';
      // Captured BEFORE the write: the page may observe its own pending write
      // while this is in flight, and the comparison below must be against the
      // version the save was based on, not that one.
      const base = this.knownVersion;
      const next = base + 1;
      try {
        await task(next);
      } catch (err) {
        if (isPermissionDenied(err)) {
          let current: number;
          try {
            current = await this.readCurrentVersion();
          } catch {
            throw err;
          }
          if (current !== base) {
            // The stored copy is at the version this write claimed and carries this write's marker: the
            // server applied it and the acknowledgement was lost. Nobody else wrote; it is saved.
            if (current === next && marker !== undefined && this.readCurrentMarker) {
              let stored: string | undefined;
              try { stored = await this.readCurrentMarker(); } catch { stored = undefined; }
              if (stored === marker) {
                this.observe(next);
                return 'saved';
              }
            }
            this.stale = true;
            return 'stale';
          }
        }
        throw err;
      }
      this.observe(next);
      return 'saved';
    }
  }
}
